import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Container,
  Nav,
  Navbar as BSNavbar,
  NavDropdown,
  Badge,
} from "react-bootstrap";
import { useAuth } from "../../context/AuthContext";
import {
  FaUser,
  FaSignOutAlt,
  FaBook,
  FaRupeeSign,
  FaUserGraduate,
  FaCalendar,
  FaHome,
  FaChartLine,
  FaHistory,
  FaLayerGroup,
  FaEnvelope,
  FaTachometerAlt,
  FaCog,
  FaUserCircle,
  FaWallet,
  FaClipboardList,
  FaVideo,
  FaCertificate,
  FaNewspaper,
  FaBars,
  FaShare,
  FaQuestionCircle,
  FaCalendarCheck,
  FaCalendarAlt,
  FaStickyNote,
  FaProjectDiagram,
  FaShoppingCart,
} from "react-icons/fa";
import { RxUpdate } from "react-icons/rx";
import InquiryService from "../../services/inquiryService";
import "../../styles/adminNavbar.css"; // New separate CSS file
import logo from "../../assets/sensible-academy-logo1.png";

const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [expanded, setExpanded] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [todayFollowUps, setTodayFollowUps] = useState(0);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [scrolled]);

  useEffect(() => {
    if (user?.role === "admin") {
      loadTodayFollowUps();
    }
  }, [user]);

  const loadTodayFollowUps = async () => {
    try {
      const response = await InquiryService.getAllInquiries({
        follow_up_today: true,
      });
      setTodayFollowUps(response.data.today_follow_ups || 0);
    } catch (error) {
      console.error("Failed to load follow-ups:", error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  if (!user) return null;

  // Admin Navigation Items
  const adminNavItems = [
    { path: "/admin", label: "Dashboard", icon: <FaTachometerAlt /> },
  ];
  // update dropdown items
  const adminUpdateDropdown = [
    { path: "/admin/courses", label: "Courses", icon: <FaBook /> },
    { path: "/admin/projects", label: "Projects", icon: <FaProjectDiagram /> },
    { path: "/admin/project-orders", label: "Project Orders", icon: <FaShoppingCart /> },
    { path: "/admin/notes", label: "Notes", icon: <FaStickyNote /> },
    { path: "/admin/batches", label: "Batches", icon: <FaLayerGroup /> },
    { path: "/admin/webinars", label: "Webinars", icon: <FaVideo /> },
    { path: "/admin/blog", label: "Blog", icon: <FaNewspaper /> },
  ];
  // Admin Dropdown Items
  const adminStudentDropdown = [
    {
      path: "/admin/students/manage",
      label: "All Students",
      icon: <FaUserGraduate />,
    },
    { path: "/admin/students", label: "Fee Management", icon: <FaRupeeSign /> },
    {
      path: "/admin/certificates",
      label: "Certificates",
      icon: <FaCertificate />,
    },
    { path: "/admin/verification", label: "Verifications", icon: <FaUser /> },
  ];

  const adminDailyDropdown = [
    {
      path: "/admin/attendance/daily",
      label: "Mark Attendance",
      icon: <FaCalendarCheck />,
    },
    {
      path: "/admin/attendance/history",
      label: "Attendance History",
      icon: <FaCalendarAlt />,
    },
    { path: "/admin/tests", label: "Tests", icon: <FaQuestionCircle /> },
  ];

  const adminCommunicationDropdown = [
    {
      path: "/admin/chat",
      label: "Chat",
      icon: <FaEnvelope />,
    },
    {
      path: "/admin/inquiries",
      label: "Inquiries",
      icon: <FaCalendar />,
      badge: todayFollowUps,
    },
    {
      path: "/admin/contact-messages",
      label: "Contact Messages",
      icon: <FaEnvelope />,
    },
    { path: "/admin/newsletter", label: "Newsletter", icon: <FaShare /> },
  ];

  // Student Navigation Items
  const studentNavItems = [
    { path: "/student", label: "Dashboard", icon: <FaHome /> },
    { path: "/student/courses", label: "My Courses", icon: <FaBook /> },
    {
      path: "/student/available",
      label: "Available Courses",
      icon: <FaChartLine />,
    },
  ];

  const studentFinanceDropdown = [
    // { path: "/student/payments", label: "Make Payment", icon: <FaCreditCard /> },
    {
      path: "/student/history",
      label: "Transaction History",
      icon: <FaHistory />,
    },
    // { path: "/student/wallet", label: "My Wallet", icon: <FaWallet /> },
  ];

  const studentLearningDropdown = [
    {
      path: "/student/assignments",
      label: "Assignments",
      icon: <FaClipboardList />,
    },
    { path: "/student/tests", label: "Tests", icon: <FaQuestionCircle /> },
    { path: "/student/notes", label: "Notes", icon: <FaStickyNote /> },
    { path: "/student/chat", label: "Chat Support", icon: <FaEnvelope /> },
    // { path: "/student/live-classes", label: "Live Classes", icon: <FaVideo /> },
    // { path: "/student/certificates", label: "My Certificates", icon: <FaCertificate /> },
  ];

  const handleLogoClick = (e) => {
    e.preventDefault();
    setExpanded(false);
    // Redirect based on user role
    if (user?.role === "admin") {
      navigate("/admin");
    } else if (user?.role === "student") {
      navigate("/student");
    }
  };

  return (
    <BSNavbar
      expand="lg"
      relative="top"
      expanded={expanded}
      onToggle={setExpanded}
      className={`admin-navbar ${scrolled ? "navbar-scrolled" : ""}`}
    >
      <Container fluid>
        {/* Logo only - big and attractive */}

        <BSNavbar.Brand
          onClick={handleLogoClick}
          className="navbar-main-brand"
          style={{ cursor: "pointer" }}
        >
          <img src={logo} alt="Sensible Academy" className="navbar-main-logo" />
        </BSNavbar.Brand>

        {/* Toggle Button */}
        <BSNavbar.Toggle
          aria-controls="admin-navbar-nav"
          className="custom-toggler"
        >
          <FaBars className="toggler-icon" />
        </BSNavbar.Toggle>

        {/* Navigation Links */}
        <BSNavbar.Collapse id="admin-navbar-nav">
          <Nav className="ms-auto align-items-center">
            {user.role === "admin" ? (
              // ========== ADMIN NAVIGATION ==========
              <>
                {adminNavItems.map((item, index) => (
                  <Nav.Link
                    key={index}
                    as={Link}
                    to={item.path}
                    className={`nav-link-custom ${isActive(item.path) ? "active" : ""}`}
                    onClick={() => setExpanded(false)}
                  >
                    <span className="nav-icon">{item.icon}</span>
                    <span className="nav-label">{item.label}</span>
                  </Nav.Link>
                ))}

                {/* updates dropdown */}
                <NavDropdown
                  title={
                    <span className="nav-link-custom d-inline-flex align-items-center">
                      <RxUpdate className="nav-icon me-2" />
                      <span className="nav-label">Updates</span>
                    </span>
                  }
                  id="students-dropdown"
                  className="custom-dropdown"
                  align="end"
                >
                  {adminUpdateDropdown.map((item, index) => (
                    <NavDropdown.Item
                      key={index}
                      as={Link}
                      to={item.path}
                      onClick={() => setExpanded(false)}
                      className={`dropdown-item-custom ${isActive(item.path) ? "active" : ""}`}
                    >
                      <span className="dropdown-icon">{item.icon}</span>
                      <span className="dropdown-label">{item.label}</span>
                    </NavDropdown.Item>
                  ))}
                </NavDropdown>

                {/* Students Dropdown */}
                <NavDropdown
                  title={
                    <span className="nav-link-custom d-inline-flex align-items-center">
                      <FaUserGraduate className="nav-icon me-2" />
                      <span className="nav-label">Students</span>
                    </span>
                  }
                  id="students-dropdown"
                  className="custom-dropdown"
                  align="end"
                >
                  {adminStudentDropdown.map((item, index) => (
                    <NavDropdown.Item
                      key={index}
                      as={Link}
                      to={item.path}
                      onClick={() => setExpanded(false)}
                      className={`dropdown-item-custom ${isActive(item.path) ? "active" : ""}`}
                    >
                      <span className="dropdown-icon">{item.icon}</span>
                      <span className="dropdown-label">{item.label}</span>
                    </NavDropdown.Item>
                  ))}
                </NavDropdown>

                {/* Communication Dropdown */}
                <NavDropdown
                  title={
                    <span className="nav-link-custom d-inline-flex align-items-center">
                      <FaEnvelope className="nav-icon me-2" />
                      <span className="nav-label">Communication</span>
                    </span>
                  }
                  id="communication-dropdown"
                  className="custom-dropdown"
                  align="end"
                >
                  {adminCommunicationDropdown.map((item, index) => (
                    <NavDropdown.Item
                      key={index}
                      as={Link}
                      to={item.path}
                      onClick={() => setExpanded(false)}
                      className={`dropdown-item-custom ${isActive(item.path) ? "active" : ""}`}
                    >
                      <span className="dropdown-icon">{item.icon}</span>
                      <span className="dropdown-label">{item.label}</span>
                      {item.badge > 0 && (
                        <Badge className="dropdown-badge">{item.badge}</Badge>
                      )}
                    </NavDropdown.Item>
                  ))}
                </NavDropdown>

                {/* Daily Activities Dropdown */}
                <NavDropdown
                  title={
                    <span className="nav-link-custom d-inline-flex align-items-center">
                      <FaCalendarAlt className="nav-icon me-2" />
                      <span className="nav-label">Daily Activities</span>
                    </span>
                  }
                  id="daily-activities-dropdown"
                  className="custom-dropdown"
                  align="end"
                >
                  {adminDailyDropdown.map((item, index) => (
                    <NavDropdown.Item
                      key={index}
                      as={Link}
                      to={item.path}
                      onClick={() => setExpanded(false)}
                      className={`dropdown-item-custom ${isActive(item.path) ? "active" : ""}`}
                    >
                      <span className="dropdown-icon">{item.icon}</span>
                      <span className="dropdown-label">{item.label}</span>
                    </NavDropdown.Item>
                  ))}
                </NavDropdown>
              </>
            ) : (
              // ========== STUDENT NAVIGATION ==========
              <>
                {studentNavItems.map((item, index) => (
                  <Nav.Link
                    key={index}
                    as={Link}
                    to={item.path}
                    className={`nav-link-custom ${isActive(item.path) ? "active" : ""}`}
                    onClick={() => setExpanded(false)}
                  >
                    <span className="nav-icon">{item.icon}</span>
                    <span className="nav-label">{item.label}</span>
                  </Nav.Link>
                ))}

                {/* Learning Dropdown */}
                <NavDropdown
                  title={
                    <span className="nav-link-custom d-inline-flex align-items-center">
                      <FaBook className="nav-icon me-2" />
                      <span className="nav-label">Learning</span>
                    </span>
                  }
                  id="learning-dropdown"
                  className="custom-dropdown"
                  align="end"
                >
                  {studentLearningDropdown.map((item, index) => (
                    <NavDropdown.Item
                      key={index}
                      as={Link}
                      to={item.path}
                      onClick={() => setExpanded(false)}
                      className={`dropdown-item-custom ${isActive(item.path) ? "active" : ""}`}
                    >
                      <span className="dropdown-icon">{item.icon}</span>
                      <span className="dropdown-label">{item.label}</span>
                    </NavDropdown.Item>
                  ))}
                </NavDropdown>

                {/* Finance Dropdown */}
                <NavDropdown
                  title={
                    <span className="nav-link-custom d-inline-flex align-items-center">
                      <FaWallet className="nav-icon me-2" />
                      <span className="nav-label">Finance</span>
                    </span>
                  }
                  id="finance-dropdown"
                  className="custom-dropdown"
                  align="end"
                >
                  {studentFinanceDropdown.map((item, index) => (
                    <NavDropdown.Item
                      key={index}
                      as={Link}
                      to={item.path}
                      onClick={() => setExpanded(false)}
                      className={`dropdown-item-custom ${isActive(item.path) ? "active" : ""}`}
                    >
                      <span className="dropdown-icon">{item.icon}</span>
                      <span className="dropdown-label">{item.label}</span>
                    </NavDropdown.Item>
                  ))}
                </NavDropdown>
              </>
            )}

            {/* User Profile Dropdown */}
            <NavDropdown
              title={
                <div className="user-profile-trigger">
                  <div className="user-avatar">
                    <FaUserCircle />
                  </div>
                  <span className="user-name">{user.name}</span>
                </div>
              }
              id="user-dropdown"
              className="user-dropdown"
              align="end"
            >
              <div className="user-dropdown-header">
                <div className="user-avatar-large">
                  <FaUserCircle />
                </div>
                <div className="user-info">
                  <h6>{user.name}</h6>
                  <p className="user-role">
                    {user.role === "admin" ? "Administrator" : "Student"}
                  </p>
                </div>
              </div>
              <NavDropdown.Divider />
              <NavDropdown.Item
                onClick={() => {
                  setExpanded(false);
                  navigate("/student/profile");
                }}
                className="dropdown-item-custom"
              >
                <FaUser className="dropdown-icon" />
                <span>My Profile</span>
              </NavDropdown.Item>
              <NavDropdown.Item
                onClick={() => {
                  setExpanded(false);
                  navigate("/settings");
                }}
                className="dropdown-item-custom"
              >
                <FaCog className="dropdown-icon" />
                <span>Settings</span>
              </NavDropdown.Item>
              <NavDropdown.Divider />
              <NavDropdown.Item
                onClick={handleLogout}
                className="dropdown-item-custom text-danger"
              >
                <FaSignOutAlt className="dropdown-icon" />
                <span>Logout</span>
              </NavDropdown.Item>
            </NavDropdown>
          </Nav>
        </BSNavbar.Collapse>
      </Container>
    </BSNavbar>
  );
};

export default Header;
